import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  focusRing,
} from '@lastrouter/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  BookOpen,
  Bot,
  ChevronDown,
  LayoutDashboard,
  Menu,
  MessageCircle,
  MessagesSquare,
  Palette,
  PanelLeftClose,
  PlugZap,
  ScrollText,
  Settings,
  Tooltip,
  Users,
  Waypoints,
} from '@lastrouter/ui';
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.js';
import { useApiQuery } from '../api/hooks.js';
import { LoadingBlock } from '../components/common.js';
import { LANGUAGES, setLanguage, type Language } from '../i18n/index.js';
import { formatDateTime } from '../lib/format.js';
import { applyBrand, applyTheme, loadNavCollapsed, loadTheme, saveNavCollapsed, type ThemePreference } from './preferences.js';
import { useSession } from './session.js';

interface NavItem {
  to: string;
  key: string;
  icon: ComponentType<{ className?: string }>;
  adminOnly: boolean;
}

interface NavGroup {
  key: string;
  items: NavItem[];
}

/**
 * Menu em grupos. O Lastrouter é a plataforma de contexto: Contexto (os apps, cada um com a sua qualidade) é o centro;
 * Canais são por onde as pessoas usam esse contexto (chat no navegador, Teams, conectores para Claude e GPT).
 * Os grupos depois do primeiro ficam fechados até a pessoa abrir ou estar numa página deles. Grupo sem item visível
 * para o papel da pessoa não aparece.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'nav.groups.context',
    items: [
      { to: '/', key: 'nav.overview', icon: LayoutDashboard, adminOnly: true },
      { to: '/clients', key: 'nav.clients', icon: BookOpen, adminOnly: false },
    ],
  },
  {
    key: 'nav.groups.channels',
    items: [
      { to: '/products/chat', key: 'nav.chat', icon: MessageCircle, adminOnly: true },
      { to: '/products/teams', key: 'nav.teams', icon: Bot, adminOnly: true },
      { to: '/products/mcp', key: 'nav.mcp', icon: Waypoints, adminOnly: true },
    ],
  },
  {
    key: 'nav.groups.audit',
    items: [
      { to: '/conversations', key: 'nav.conversations', icon: MessagesSquare, adminOnly: false },
      { to: '/audit', key: 'nav.audit', icon: ScrollText, adminOnly: true },
    ],
  },
  {
    key: 'nav.groups.admin',
    items: [
      { to: '/users', key: 'nav.users', icon: Users, adminOnly: true },
      { to: '/connections', key: 'nav.connections', icon: PlugZap, adminOnly: true },
      { to: '/appearance', key: 'nav.appearance', icon: Palette, adminOnly: true },
      { to: '/settings', key: 'nav.settings', icon: Settings, adminOnly: true },
    ],
  },
];

const isActive = (to: string, path: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));

/** Texto que some com o menu recolhido. Continua no leitor de tela, que lê o nome do item. */
const fade = (collapsed: boolean) => cn('lr-nav-fade', collapsed && 'pointer-events-none -translate-x-1 opacity-0');

/** Marca do cliente (pública): vale também na tela de entrar. */
export function useBrand() {
  const q = useApiQuery('publicAppearance', undefined, { staleTime: 300_000 });
  useEffect(() => {
    if (q.data) applyBrand(q.data);
  }, [q.data]);
  return q.data;
}

function BrandMark({ name, logo, collapsed = false }: { name: string; logo: string | null; collapsed?: boolean }) {
  return (
    <div className={cn('flex min-w-0 items-center gap-2 px-3 py-4', fade(collapsed))}>
      {logo ? (
        <img src={logo} alt="" className="h-7 w-auto max-w-24 object-contain" />
      ) : (
        <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-[var(--lr-radius-md)] bg-accent text-accent-fg">
          <Activity className="size-4" />
        </span>
      )}
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate font-semibold text-fg">{name}</span>
        <span className="text-[length:var(--lr-text-xs)] text-fg-muted">Lastrouter</span>
      </div>
    </div>
  );
}

function NavGroups({ groups, collapsed = false, onNavigate }: { groups: NavGroup[]; collapsed?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [bar, setBar] = useState<{ top: number; height: number } | null>(null);
  // Grupos que a pessoa abriu ou fechou à mão; sem escolha, abre o da página atual.
  const [open, setOpen] = useState<Map<string, boolean>>(new Map());
  // Na primeira posição o indicador já nasce no lugar; só depois ele desliza.
  const [animate, setAnimate] = useState(false);

  // Indicador do item ativo: um fundo só, que desliza até o item da página atual.
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const place = () => {
      // Item de grupo fechado não ganha o fundo: ele está escondido.
      const a = el.querySelector<HTMLElement>('.lr-nav-group[data-open="true"] [aria-current="page"]');
      setBar(a ? { top: a.offsetTop, height: a.offsetHeight } : null);
    };
    place();
    const frame = requestAnimationFrame(() => setAnimate(true));
    const ro = new ResizeObserver(place);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [path, collapsed, open]);

  return (
    <div ref={root} className="relative px-2">
      <span
        aria-hidden="true"
        className={cn(animate && 'lr-nav-indicator', 'pointer-events-none absolute left-2 right-2 top-0 rounded-[var(--lr-radius-md)] bg-accent-soft before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-accent')}
        style={{ transform: `translateY(${bar?.top ?? 0}px)`, height: bar?.height ?? 0, opacity: bar ? 1 : 0 }}
      />
      {groups.map((g, i) => {
        // O primeiro grupo abre de saída; os outros, quando a pessoa abre ou está numa página deles.
        const shown = collapsed || (open.get(g.key) ?? (i === 0 || g.items.some((n) => isActive(n.to, path))));
        const toggle = () => setOpen((o) => new Map(o).set(g.key, !shown));
        return (
        <div key={g.key} className="mb-1">
          <div className="relative">
            <button
              type="button"
              id={`nav-${g.key}`}
              aria-expanded={shown}
              tabIndex={collapsed ? -1 : undefined}
              onClick={toggle}
              className={cn('flex w-full items-center gap-2.5 whitespace-nowrap rounded-[var(--lr-radius-md)] px-3 py-2 font-medium text-fg-secondary hover:bg-sunken hover:text-fg active:bg-border', focusRing, fade(collapsed))}
            >
              {t(g.key)}
              <ChevronDown aria-hidden="true" className={cn('lr-nav-toggle-icon ml-auto size-4 text-fg-muted', !shown && '-rotate-90')} />
            </button>
            <span aria-hidden="true" className={cn('lr-nav-fade absolute inset-x-3 top-1/2 h-px bg-border', !collapsed && 'opacity-0')} />
          </div>
          {/* Sanfona: a altura vai de 0 ao conteúdo com fade; fechado, o grupo sai do Tab (inert). */}
          <div className="lr-nav-group" data-open={shown} inert={!shown}>
            <div>
          <ul aria-labelledby={`nav-${g.key}`} className="flex flex-col gap-0.5">
            {g.items.map((item) => {
              const link = (
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  activeOptions={{ exact: item.to === '/' }}
                  className={cn(
                    'group relative flex items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-[var(--lr-radius-md)] px-3 py-2 text-fg-secondary hover:bg-sunken hover:text-fg active:bg-border aria-[current=page]:font-medium aria-[current=page]:text-fg aria-[current=page]:hover:bg-transparent',
                    focusRing,
                  )}
                  activeProps={{ 'aria-current': 'page' }}
                >
                  <item.icon className="lr-nav-icon size-4 shrink-0 motion-safe:group-hover:-rotate-6 motion-safe:group-hover:scale-115" aria-hidden="true" />
                  <span className={fade(collapsed)}>{t(item.key)}</span>
                </Link>
              );
              return (
                <li key={item.to}>
                  {collapsed ? (
                    <Tooltip content={t(item.key)} side="right">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function initials(text: string): string {
  const parts = text.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function UserMenu({ user, name, isAdmin, expiresAt, collapsed = false }: { user: string; name: string | null; isAdmin: boolean; expiresAt: string; collapsed?: boolean }) {
  const { t, i18n } = useTranslation();
  const [theme, setTheme] = useState<ThemePreference>(loadTheme());
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('user.menu')}
          className={cn('flex w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-[var(--lr-radius-md)] px-2.5 py-2 text-left hover:bg-sunken active:bg-border', focusRing)}
        >
          <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[length:var(--lr-text-xs)] font-semibold text-fg">
            {initials(name ?? user)}
          </span>
          <span className={cn('flex min-w-0 flex-1 flex-col leading-tight', fade(collapsed))}>
            <span className="truncate font-medium text-fg">{name ?? user}</span>
            <span className="truncate text-[length:var(--lr-text-xs)] text-fg-muted">{isAdmin ? t('user.admin') : t('user.owner')}</span>
          </span>
          <ChevronDown className={cn('size-4 shrink-0 text-fg-muted', fade(collapsed))} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-60">
        <DropdownMenuLabel>
          {user}
          <br />
          {t('user.expires', { time: formatDateTime(expiresAt) })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('user.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(v) => {
            setTheme(v as ThemePreference);
            applyTheme(v as ThemePreference);
          }}
        >
          <DropdownMenuRadioItem value="system">{t('user.themeSystem')}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">{t('user.themeLight')}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">{t('user.themeDark')}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('user.language')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={i18n.language} onValueChange={(v) => setLanguage(v as Language)}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code}>
              {l.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await api('logout').catch(() => undefined);
            qc.clear();
            await navigate({ to: '/login' });
          }}
        >
          {t('user.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Casca do painel: menu lateral, conteúdo e a regra de acesso por papel. */
export function AppShell() {
  const { t } = useTranslation();
  const session = useSession();
  const brand = useBrand();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(loadNavCollapsed);
  const path = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    applyTheme(loadTheme());
  }, []);

  if (session.isPending) {
    return (
      <div className="p-8">
        <LoadingBlock rows={6} />
      </div>
    );
  }
  if (session.error || !session.data) return <Navigate to="/login" />;

  const s = session.data;
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => s.is_admin || !n.adminOnly) })).filter((g) => g.items.length > 0);
  // Dono de assunto sem papel de admin começa nos apps.
  if (!s.is_admin && path === '/') return <Navigate to="/clients" />;

  const toggleNav = () => {
    saveNavCollapsed(!collapsed);
    setCollapsed(!collapsed);
  };
  const toggleLabel = collapsed ? t('nav.expand') : t('nav.collapse');

  return (
    <div className="min-h-screen bg-page lg:grid lg:grid-cols-[var(--nav-w)_minmax(0,1fr)]" style={{ '--nav-w': collapsed ? '64px' : '240px' } as CSSProperties}>
      <a href="#content" className={cn('sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-[var(--lr-radius-md)] focus:bg-surface focus:px-3 focus:py-2', focusRing)}>
        {t('nav.skip')}
      </a>
      <aside className="hidden overflow-x-hidden border-r border-border bg-surface lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col">
        <div className="relative pr-12">
          <BrandMark name={brand?.assistant_name ?? 'Lastrouter'} logo={brand?.logo ?? null} collapsed={collapsed} />
          {/* right-4 com 32px de largura fica centrado na faixa de 64px do menu recolhido. */}
          <Tooltip content={toggleLabel} side="right">
            <button
              type="button"
              onClick={toggleNav}
              aria-expanded={!collapsed}
              aria-controls="menu-lateral"
              aria-label={toggleLabel}
              className={cn('absolute right-4 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-[var(--lr-radius-md)] text-fg-secondary hover:bg-sunken hover:text-fg active:bg-border', focusRing)}
            >
              <PanelLeftClose className={cn('lr-nav-toggle-icon size-4', collapsed && 'rotate-180')} aria-hidden="true" />
            </button>
          </Tooltip>
        </div>
        <nav id="menu-lateral" aria-label={t('nav.label')} className="flex-1 overflow-y-auto overflow-x-hidden">
          <NavGroups groups={groups} collapsed={collapsed} />
          {!s.is_admin && !collapsed ? <p className="mx-4 mt-4 text-[length:var(--lr-text-xs)] text-fg-muted">{t('nav.ownerNotice')}</p> : null}
        </nav>
        <div className="border-t border-border p-2">
          <UserMenu user={s.user} name={s.name} isAdmin={s.is_admin} expiresAt={s.expires_at} collapsed={collapsed} />
        </div>
      </aside>

      <div className="flex items-center justify-between border-b border-border bg-surface px-2 lg:hidden">
        <BrandMark name={brand?.assistant_name ?? 'Lastrouter'} logo={brand?.logo ?? null} />
        <Button variant="ghost" size="icon" aria-expanded={menuOpen} aria-controls="menu-movel" aria-label={t('nav.label')} onClick={() => setMenuOpen((v) => !v)}>
          <Menu />
        </Button>
      </div>
      {menuOpen ? (
        <nav id="menu-movel" aria-label={t('nav.label')} className="lr-enter border-b border-border bg-surface py-2 lg:hidden">
          <NavGroups groups={groups} onNavigate={() => setMenuOpen(false)} />
          <div className="mt-2 border-t border-border p-2">
            <UserMenu user={s.user} name={s.name} isAdmin={s.is_admin} expiresAt={s.expires_at} />
          </div>
        </nav>
      ) : null}

      <main id="content" tabIndex={-1} className="mx-auto w-full max-w-[1200px] px-4 py-6 outline-none lg:px-8 lg:py-8">
        {/* Troca de página: o conteúdo novo entra com fade e um leve deslize (sem animação de saída, nada pisca). */}
        <div key={path} className="lr-page">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/** Página só de administrador: dono de assunto vê a explicação, não um erro. */
export function AdminOnly({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const s = useSession().data;
  if (s && !s.is_admin) return <p className="text-fg-secondary">{t('errors.forbidden')}</p>;
  return <>{children}</>;
}
